import React, { useEffect, useState } from 'react';
import { recipeApi, translationApi } from '../api.js';
import { getTranslatedText, getTranslationCompleteness, getAvailableLanguages, TranslatableRecipe } from '../translationHelpers.js';

interface RecipeIngredient {
  id: string;
  name: string;
  name_translations?: { [languageCode: string]: string };
  ingredient?: {
    id: string;
    name: string;
    name_translations?: { [languageCode: string]: string };
  };
}

interface Recipe extends TranslatableRecipe {
  id: string;
  name: string;
  description?: string;
  image?: string;
  recipeImage?: { imageUrl?: string } | null;
  drinkFeatures?: Array<{ property: string; val: number }>;
  recipeIngredient?: RecipeIngredient[];
  name_translations?: { [languageCode: string]: string };
  description_translations?: { [languageCode: string]: string };
}

interface RecipePreviewProps {
  shopId: string;
  showIngredients?: boolean;
  showTranslations?: boolean;
  selectedLanguage?: string;
  darkTheme?: {
    text: string;
    textMuted: string;
    border: string;
    surface: string;
    success: string;
    warning: string;
  };
}

const defaultTheme = {
  text: '#e5e7eb',
  textMuted: '#9ca3af',
  border: '#222',
  surface: '#181c20',
  success: '#27d0c6',
  warning: '#f59e0b',
};


export const RecipePreview: React.FC<RecipePreviewProps> = ({ 
  shopId, 
  showIngredients = true, 
  showTranslations = true, 
  selectedLanguage = '', 
  darkTheme = defaultTheme 
}) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [savingTranslation, setSavingTranslation] = useState<boolean>(false);

  useEffect(() => {
    if (!shopId) return;
    setLoading(true);
    setError(null);
    recipeApi.getAdminRecipes(shopId)
      .then((resp: { data?: { recipes?: Recipe[] } | Recipe[] | string }) => {
        let data: Recipe[];
        if (!resp.data) {
          data = [];
        } else if (typeof resp.data === 'string') {
          try {
            data = JSON.parse(resp.data) as Recipe[];
          } catch {
            data = [];
          }
        } else if (Array.isArray(resp.data)) {
          data = resp.data;
        } else if (typeof resp.data === 'object' && 'recipes' in resp.data) {
          data = resp.data.recipes || [];
        } else {
          data = [];
        }
        setRecipes(Array.isArray(data) ? data : []);
      })
      .catch((err: Error) => {
        setError('Failed to load recipes');
        setRecipes([]);
      })
      .finally(() => setLoading(false));
  }, [shopId]);

  const handleEditTranslation = (fieldId: string, currentValue: string) => {
    setEditingField(fieldId);
    setEditingValue(currentValue);
  };

  const handleSaveTranslation = async (recipeId: string, fieldName: string, entityType: 'recipe' | 'ingredient', entityId?: string) => {
    if (!selectedLanguage || !editingValue.trim()) return;
    
    setSavingTranslation(true);
    try {
      if (entityType === 'recipe') {
        await translationApi.updateRecipeTranslation(shopId, recipeId, fieldName, selectedLanguage, editingValue.trim());
      } else if (entityType === 'ingredient' && entityId) {
        await translationApi.updateIngredientTranslation(shopId, entityId, selectedLanguage, editingValue.trim());
      }
      
      // Refresh recipes to show updated translation
      const resp = await recipeApi.getAdminRecipes(shopId);
      let data: Recipe[];
      if (!resp.data) {
        data = [];
      } else if (typeof resp.data === 'string') {
        try {
          data = JSON.parse(resp.data) as Recipe[];
        } catch {
          data = [];
        }
      } else if (Array.isArray(resp.data)) {
        data = resp.data;
      } else if (typeof resp.data === 'object' && 'recipes' in resp.data) {
        data = resp.data.recipes || [];
      } else {
        data = [];
      }
      setRecipes(Array.isArray(data) ? data : []);
      
      setEditingField(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error saving translation:', error);
      alert('Failed to save translation. Please try again.');
    } finally {
      setSavingTranslation(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditingValue('');
  };

  if (loading) return <div>Loading recipes...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div>
      <h4 style={{ color: darkTheme.text }}>Recipes ({recipes.length})</h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
        {recipes.map((recipe) => (
          <div
            key={recipe.id}
            style={{
              border: `1px solid ${darkTheme.border}`,
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: darkTheme.surface,
            }}
          >
            {((recipe.image || (recipe.recipeImage && recipe.recipeImage.imageUrl))) && (
              <img
                src={recipe.image ? recipe.image : (recipe.recipeImage && recipe.recipeImage.imageUrl ? recipe.recipeImage.imageUrl : '')}
                alt={recipe.name}
                style={{
                  width: '100%',
                  height: '150px',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  marginBottom: '12px',
                }}
              />
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              {editingField === `recipe_${recipe.id}_name` ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <input
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      fontSize: '14px',
                      border: `1px solid ${darkTheme.border}`,
                      borderRadius: '4px',
                      backgroundColor: darkTheme.surface,
                      color: darkTheme.text
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveTranslation(recipe.id, 'name', 'recipe');
                      } else if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => handleSaveTranslation(recipe.id, 'name', 'recipe')}
                    disabled={savingTranslation}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      backgroundColor: darkTheme.success,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      backgroundColor: darkTheme.textMuted,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <h4 style={{ margin: 0, fontSize: '16px', color: darkTheme.text, flex: 1 }}>
                    {selectedLanguage ? getTranslatedText(recipe.name, recipe.name_translations, selectedLanguage) : recipe.name}
                  </h4>
                  {selectedLanguage && showTranslations && (
                    <button
                      onClick={() => handleEditTranslation(
                        `recipe_${recipe.id}_name`,
                        getTranslatedText(recipe.name, recipe.name_translations, selectedLanguage)
                      )}
                      style={{
                        padding: '2px 6px',
                        fontSize: '10px',
                        backgroundColor: 'transparent',
                        color: darkTheme.textMuted,
                        border: `1px solid ${darkTheme.border}`,
                        borderRadius: '3px',
                        cursor: 'pointer',
                        marginLeft: '8px'
                      }}
                      title="Edit translation"
                    >
                      ✏️
                    </button>
                  )}
                </>
              )}
            </div>
            
            {recipe.description && (
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: darkTheme.textMuted }}>
                {selectedLanguage ? getTranslatedText(recipe.description, recipe.description_translations, selectedLanguage) : recipe.description}
              </p>
            )}

            {showTranslations && (
              <div style={{ marginBottom: '12px' }}>
                {(() => {
                  const availableLanguages = getAvailableLanguages(recipe);
                  const completeness = selectedLanguage ? getTranslationCompleteness(recipe, selectedLanguage) : 0;
                  
                  return (
                    <div style={{ fontSize: '12px' }}>
                      {availableLanguages.length > 0 ? (
                        <div style={{ color: darkTheme.success }}>
                          ✓ Translations: {availableLanguages.join(', ')}
                          {selectedLanguage && (
                            <span style={{ marginLeft: '8px', color: completeness === 100 ? darkTheme.success : darkTheme.warning }}>
                              ({completeness}% complete)
                            </span>
                          )}
                        </div>
                      ) : (
                        <div style={{ color: darkTheme.textMuted }}>
                          No translations available
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {showIngredients && recipe.recipeIngredient && recipe.recipeIngredient.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: darkTheme.text, marginBottom: '6px' }}>
                  Ingredients ({recipe.recipeIngredient.length}):
                </div>
                <div style={{ fontSize: '12px', color: darkTheme.textMuted }}>
                  {recipe.recipeIngredient.map((ri, idx) => {
                    const ingredientName = ri.ingredient?.name || ri.name;
                    const ingredientTranslations = ri.ingredient?.name_translations || ri.name_translations;
                    const displayName = selectedLanguage ? 
                      getTranslatedText(ingredientName, ingredientTranslations, selectedLanguage) : 
                      ingredientName;
                    
                    const hasTranslation = selectedLanguage && ingredientTranslations?.[selectedLanguage];
                    const ingredientId = ri.ingredient?.id || ri.id || `${recipe.id}_ingredient_${idx}`;
                    const fieldId = `ingredient_${ingredientId}_name`;
                    
                    return (
                      <div key={ri.id || idx} style={{ 
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        {editingField === fieldId ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                            <input
                              type="text"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              style={{
                                flex: 1,
                                padding: '2px 6px',
                                fontSize: '12px',
                                border: `1px solid ${darkTheme.border}`,
                                borderRadius: '3px',
                                backgroundColor: darkTheme.surface,
                                color: darkTheme.text
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveTranslation(recipe.id, 'name', 'ingredient', ingredientId);
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveTranslation(recipe.id, 'name', 'ingredient', ingredientId)}
                              disabled={savingTranslation}
                              style={{
                                padding: '2px 4px',
                                fontSize: '10px',
                                backgroundColor: darkTheme.success,
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: 'pointer'
                              }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                padding: '2px 4px',
                                fontSize: '10px',
                                backgroundColor: darkTheme.textMuted,
                                color: 'white',
                                border: 'none',
                                borderRadius: '2px',
                                cursor: 'pointer'
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <>
                            <span style={{ flex: 1 }}>{displayName}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {selectedLanguage && showTranslations && (
                                <button
                                  onClick={() => handleEditTranslation(fieldId, displayName)}
                                  style={{
                                    padding: '1px 3px',
                                    fontSize: '8px',
                                    backgroundColor: 'transparent',
                                    color: darkTheme.textMuted,
                                    border: `1px solid ${darkTheme.border}`,
                                    borderRadius: '2px',
                                    cursor: 'pointer'
                                  }}
                                  title="Edit ingredient translation"
                                >
                                  ✏️
                                </button>
                              )}
                              {showTranslations && selectedLanguage && (
                                <span style={{ 
                                  fontSize: '10px',
                                  color: hasTranslation ? darkTheme.success : darkTheme.textMuted,
                                  minWidth: '12px'
                                }}>
                                  {hasTranslation ? '✓' : '○'}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {recipe.drinkFeatures && recipe.drinkFeatures.length > 0 && (
              <div style={{ fontSize: '12px', color: darkTheme.textMuted }}>
                <strong>Features:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                  {recipe.drinkFeatures.map((feature, idx) => (
                    <li key={idx}>
                      {feature.property}: {feature.val}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
